import { create } from 'zustand';

import { mergeArchive, readArchiveManifest, summariseMerge } from '@/features/backup';
import { useImportQueue } from '@/features/import';

/**
 * Every route by which a file arrives from outside the app — Open With and
 * a double-click in Finder or Explorer (desktop app), Open With on an
 * installed web app, Android's share sheet — ends here.
 *
 * Track files join the ordinary import queue, the same as a drop. An `.fdv`
 * is merged: a sync file (its manifest names the device that wrote it) the
 * way a sync would, and a backup the way Restore would. Either way the
 * result is said once, in `OpenedFileNotice`.
 *
 * In `app/` because it joins two features, which neither may do for itself.
 */

interface OpenedFileState {
  message: string | null;
  tone: 'info' | 'error';
}

export const useOpenedFileNotice = create<OpenedFileState>(() => ({ message: null, tone: 'info' }));

export function isArchive(file: File): boolean {
  return file.name.toLowerCase().endsWith('.fdv');
}

export async function openFiles(files: readonly File[]): Promise<void> {
  const tracks = files.filter((file) => !isArchive(file));
  if (tracks.length > 0) {
    void useImportQueue.getState().enqueue(tracks);
  }
  for (const file of files.filter(isArchive)) {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const mode = readArchiveManifest(bytes).device === undefined ? 'restore' : 'sync';
      const summary = await mergeArchive(bytes, { mode });
      useOpenedFileNotice.setState({
        message: `${file.name}: ${summariseMerge(summary)}`,
        tone: 'info',
      });
    } catch (error) {
      useOpenedFileNotice.setState({
        message: `${file.name}: ${error instanceof Error ? error.message : 'File could not be opened.'}`,
        tone: 'error',
      });
    }
  }
}
